import {listEndpoint, type ListEndpoint} from "./util/ListEndpoint.ts";
import type {AxiosInstance} from "axios";
import {type MetlGroupApi, metlGroupApi} from "./MetlGroupApi.ts";
import {sharedJobPackageApi, type SharedJobPackageApi} from "./MetlSharedJobApi.ts";
import {metlDriversApi, type MetlDriversApi} from "./MetlDriversApi.ts";
import {checkScheme, SchemeError} from "./util/SchemeCheck.ts";
import type {ProjectId} from "../metl-models/id/ProjectId.tsx";
import type {MetlProjectApi} from "./MetlProjectApi.ts";
import type {SupportInfoScan} from "../analysis-process/3-perform-scan/scan-model/ScanResult.tsx";
import {type MetlApiExtractExport, type MetlApiQueryApi, metlApiQueryEndpoint} from "./MetlApiQueryApi.tsx";
import type {MetlOAuthExport} from "../metl-models/exports/MetlOAuthExport.ts";
import {metlCredentialsApi, type MetlCredentialsApi} from "./MetlCredentialsApi.ts";
import {metlQueueApi, type MetlQueueApi} from "./MetlQueueApi.ts";

export interface MetlApiBase extends MetlApi {
    drivers: ()=>MetlDriversApi
    groups: ListEndpoint<MetlGroupApi>,
    sharedJobs: ListEndpoint<SharedJobPackageApi>
    test: ()=>Promise<boolean>,
    supportInfo: ()=>Promise<SupportInfoScan>
    credentials: () => MetlCredentialsApi
    project: (projectId: ProjectId)=>MetlProjectApi
    apiQuery: ListEndpoint<MetlApiQueryApi>
    apiExtract: ListEndpoint<MetlApiQueryApi, MetlApiExtractExport>
    oauth: ListEndpoint<MetlApiQueryApi, MetlOAuthExport>
    queue: () => MetlQueueApi
}

export function metlApi(axios: AxiosInstance): MetlApiBase {
    const id = axios.defaults.baseURL ?? 'unknown'
    const groups = listEndpoint(axios, '/groups', `${id}/groups`, metlGroupApi)
    return {
        id,
        drivers: () => metlDriversApi(axios, id),
        groups,
        sharedJobs: listEndpoint(axios, '/sharedJob/package', `${id}/shared-jobs`, sharedJobPackageApi),
        apiQuery: listEndpoint(axios, '/apiprofile', `${id}/apiprofile`, metlApiQueryEndpoint),
        apiExtract: listEndpoint(axios, '/apiextractprofile', `${id}/apiextract`, metlApiQueryEndpoint),
        oauth: listEndpoint(axios, '/oauth', `${id}/oauth`, metlApiQueryEndpoint),
        queue: () => metlQueueApi(axios, '/queue', `${id}/queue`),
        credentials: () => metlCredentialsApi(axios, '/credentials', `${id}/credentials`),
        project: (projectId) => groups.get(projectId.groupName).projects.get(projectId.projectName),
        supportInfo: async () => {
            return axios.get<SupportInfoScan>('/metadata/info').then(r => r.data)
        },
        test: async () => {
            const uri = axios.defaults.baseURL ?? ''
            // Check that the uri scheme and the current window scheme are the same and throw a detailed error if they are not
            const [validScheme, desiredScheme, currentScheme] = checkScheme(uri)
            if(!validScheme) {
                throw new SchemeError(`Failed API Test. You are trying to connect a ${currentScheme} URL to a ${desiredScheme} API.`, desiredScheme)
            }
            await axios.get<unknown>('').then(r => {
                if(r.status === 200) return true
                if(r.status) {
                    throw new Error(`Failed API Test. Status code: ${r.status}.`)
                }
            }).catch(err => {
                console.log(err)
                throw new Error(`Failed API Test. ${err}`)
            })
            return true
        }
    }
}

export interface MetlApi {
    /** an id which identifies an api instance for use in query caching. If a detail in the api (e.g. the project name) changes, this id should change */
    id: string
}